'use client'

import { createContext, useContext, useEffect, useState } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'

import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'
import { mapRoute } from '@dfds-pax/utils'

import { BasketError } from '@/app/[locale]/[ferry]/_actions/checkBasketValidity'
import { getBasketEnriched } from '@/app/[locale]/[ferry]/_actions/getBasketEnriched'
import { saveSurveyBasketData } from '@/app/[locale]/[ferry]/_lib/surveyHandler'
import { SelectedAddon } from '@/app/[locale]/[ferry]/extras/_components/interface'
import { SelectedPet } from '@/app/[locale]/[ferry]/pets/_components/interface'
import { createBasket } from '@/app/[locale]/[ferry]/yourbooking/_actions/createBasket'
import { toggleResidentVehicle } from '@/app/[locale]/[ferry]/yourbooking/_actions/toggleResidentVehicle'
import {
  selectedAddonsAtom,
  selectedDateAtom,
  selectedDeparturesAtom,
  selectedOfferAtom,
  selectedPassengersAtom,
  selectedPetsAtom,
  selectedRoutesAtom,
  selectedVehiclesAtom,
} from '@/states/selectionAtom'

import {
  CommercialDiscount,
  getSessionBasket,
  getSessionCommercialDiscount,
  getUrlLocale,
  setSession,
  setSessionBasket,
  setSessionCommercialDiscount,
} from '@/utils'
import { constructYourBookingUrl } from '@/utils/urlHelpers'

import { updateYourBookingUrl } from '@/hooks/updateYourBookingUrl'
import { wipeSessions } from '@/hooks/useCleanSession'

type BasketContextType = {
  basket: BffInterfacesBasket.Basket | null
  basketId: string
  basketInitiated: boolean
  basketLoading: boolean
  commercialDiscount: CommercialDiscount | null
  depDateHomebound: string | null
  recoveredBasketErrors: BffInterfacesBasket.RecoveredBasketErrors | null
  residentSwitchLoading: boolean
  residentSwitchNonce: number
  routeHomebound: string | null
  toggleSogResidentVehicle: (checked: boolean) => Promise<BasketError | null>
  updateBasket: () => void
}

type BasketProviderProps = {
  children: React.ReactNode
}

const BasketContext = createContext<BasketContextType | undefined>(undefined)

export const useBasket = (): BasketContextType => {
  const context = useContext(BasketContext)
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider')
  }
  return context
}

export const BasketProvider = ({ children }: BasketProviderProps) => {
  const [basket, setBasket] = useState<BffInterfacesBasket.Basket | null>(null)
  const [basketId, setBasketId] = useState<string>('')
  const [basketInitiated, setBasketInitiated] = useState<boolean>(false)
  const [basketLoading, setBasketLoading] = useState<boolean>(false)
  const [urlParamsLoaded, setUrlParamsLoaded] = useState<boolean>(false)
  const [urlDepartures, setUrlDepartures] = useState<{ depDateHomebound: string | null; routeHomebound: string | null }>({
    depDateHomebound: null,
    routeHomebound: null,
  })
  const [recoveredBasketErrors, setRecoveredBasketErrors] = useState<BffInterfacesBasket.RecoveredBasketErrors | null>(null)
  // Resident pricing is a commercial discount that arrives on the yourbooking URL and is kept in
  // session storage, since the URL is wiped once it has been read.
  const [commercialDiscount, setCommercialDiscount] = useState<CommercialDiscount | null>(null)
  // Bumped whenever a resident-vehicle toggle fails. The resident Switch is controlled but keeps its
  // own optimistic internal state and only re-syncs when its `checked` value changes. On a failed
  // toggle the basket (and therefore `checked`) is unchanged, so the switch wouldn't revert on its
  // own. Using this value in the Switch's `key` forces a remount that re-syncs it to the basket.
  const [residentSwitchNonce, setResidentSwitchNonce] = useState<number>(0)
  // True only while a resident-vehicle toggle is rebuilding the basket, so the yourbooking page can
  // show its skeleton for the toggle specifically (basketLoading is also set by other refreshes).
  const [residentSwitchLoading, setResidentSwitchLoading] = useState<boolean>(false)
  const [search, setSearch] = useState(() => (typeof window !== 'undefined' ? window.location.search : ''))

  const selectedRoutes = useAtomValue(selectedRoutesAtom)
  const selectedVehicles = useAtomValue(selectedVehiclesAtom)
  const selectedPassengers = useAtomValue(selectedPassengersAtom)
  const selectedDates = useAtomValue(selectedDateAtom)
  const selectedPets = useAtomValue(selectedPetsAtom)
  const selectedOffer = useAtomValue(selectedOfferAtom)
  const setSelectedPets = useSetAtom(selectedPetsAtom)
  const setSelectedAddons = useSetAtom(selectedAddonsAtom)
  const setSelectedDepartures = useSetAtom(selectedDeparturesAtom)

  // Listen for changes to the search string
  useEffect(() => {
    const handler = () => setSearch(window.location.search)
    const _fetchBasket = async () => {
      const _basketId = getSessionBasket()?.basketId
      if (_basketId) {
        // eslint-disable-next-line react-hooks/immutability
        await fetchBasket(_basketId)
      }
    }
    window.addEventListener('popstate', handler)
    window.addEventListener('pushstate', handler)
    window.addEventListener('replacestate', handler)
    window.addEventListener('updateSummary', _fetchBasket)
    return () => {
      window.removeEventListener('popstate', handler)
      window.removeEventListener('pushstate', handler)
      window.removeEventListener('replacestate', handler)
      window.removeEventListener('updateSummary', _fetchBasket)
    }
  }, [])

  //Fetch or create basket when url params are loaded
  useEffect(() => {
    if (
      !basket &&
      urlParamsLoaded &&
      selectedDates.outboundDepartureDate &&
      selectedVehicles.vehicleCode &&
      selectedPassengers.adults &&
      selectedRoutes.outbound
    ) {
      // eslint-disable-next-line react-hooks/immutability
      getOrCreateBasket()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoutes, selectedVehicles, selectedPassengers, selectedDates, search, urlParamsLoaded])

  //Handle url params and resets when starting a new booking
  useEffect(() => {
    const urlParams = new URLSearchParams(search)
    const basketUrlId = urlParams.get('basketId')
    const sessionId = urlParams.get('sessionId')
    const routeOut = urlParams.get('routeOut')

    const loadUrlParams = async () => {
      const { commercialDiscount, depDateHomebound, recoveredBasketErrors, routeHomebound } = await updateYourBookingUrl()
      setUrlDepartures({ depDateHomebound: depDateHomebound, routeHomebound: routeHomebound })
      setRecoveredBasketErrors(recoveredBasketErrors)
      setCommercialDiscount(commercialDiscount)
      setUrlParamsLoaded(true)
    }
    //if one of these params are present, we clean the session and load the url params
    if ((basketUrlId && sessionId) || routeOut) {
      wipeSessions()
      loadUrlParams()
    } else {
      setCommercialDiscount(getSessionCommercialDiscount())
      setUrlParamsLoaded(true)
    }
  }, [search])

  async function getOrCreateBasket(): Promise<void> {
    setBasketLoading(true)
    const urlParams = new URLSearchParams(window.location.search)
    const routeOut = urlParams.get('routeOut')
    const basketId = urlParams.get('basketId')
    const _sessionId = urlParams.get('sessionId')
    const { sessionId } = _sessionId ? setSession(_sessionId) : setSession()
    if (
      (!basketId || routeOut) &&
      window.location.pathname.includes('/yourbooking') &&
      selectedRoutes.outbound &&
      selectedRoutes.outbound !== 'undefined' &&
      selectedVehicles.vehicleCode &&
      selectedPassengers.adults &&
      selectedDates.outboundDepartureDate
    ) {
      //if no basketId or a new basket is needed, we create a new basket
      const ports = mapRoute(selectedRoutes.outbound)
      const hasPets = selectedPets.showNumberOfPets > 0
      const body = {
        numberOfAdults: selectedPassengers.adults.toString(),
        sessionId,
        trailerTypeCode: selectedVehicles?.trailerCode ?? '',
        vehicleTypeCode: selectedVehicles.vehicleCode,
        ...(selectedPassengers?.childrenAges.length > 0
          ? {
              agesOfChildren: decodeURIComponent(selectedPassengers.childrenAges.toString()),
            }
          : undefined),
        commercialDiscount: commercialDiscount ?? undefined,
        hasPets,
        homeDepartureDate: selectedDates.returnDepartureDate,
        homeDeparturePort: ports.arrivalUnLoCode,
        initialOfferCode: selectedOffer?.offerCode ?? undefined,
        locale: getUrlLocale(),
        outDepartureDate: selectedDates.outboundDepartureDate,
        outDeparturePort: ports.departureUnLoCode,
      }

      const { basketId } = await createBasket(body)
      sessionStorage.setItem('isTravelling', JSON.stringify('true'))
      const basket = await fetchBasket(basketId)
      saveSurveyBasketData(basket)
      const yourbookUrl = constructYourBookingUrl()
      sessionStorage.setItem('restartBookingUrl', yourbookUrl)
      setBasketId(basketId)
      setSessionBasket(basketId)
      setBasketInitiated(true)
      setBasketLoading(false)
      //We update the URL to include the new basketId so that it can be used in subsequent requests.
      window.history.replaceState({}, '', window.location.pathname + '?basketId=' + basketId)
    } else if (basketId && !basket) {
      const basket = await fetchBasket(basketId)
      saveSurveyBasketData(basket)
      setBasketId(basketId)
      setBasketInitiated(true)
      setBasketLoading(false)
    } else {
      // Never happens
    }
  }

  async function toggleSogResidentVehicle(checked: boolean): Promise<BasketError | null> {
    if (!basket) return null

    setBasketLoading(true)
    setResidentSwitchLoading(true)

    try {
      const result = await toggleResidentVehicle(checked, getUrlLocale(), basketId)

      // The toggle couldn't be applied — a selected leg couldn't be re-priced with the new discount,
      // so the action deliberately did NOT create a new basket. Keep the current basket and bump the
      // nonce so the Switch remounts and reverts to the unchanged state. A basketError (e.g. sales
      // closed while re-adding extras) is returned so the caller can surface the modal.
      if (result.status === 'failed') {
        setResidentSwitchNonce((nonce) => nonce + 1)
        return result.basketError ?? null
      }

      const { basketId: newBasketId } = result
      const newCommercialDiscount = checked ? 'resident' : null

      // A new basket was created — point the session at it and fully re-sync the frontend state.
      setSessionBasket(newBasketId)
      setSessionCommercialDiscount(newCommercialDiscount)
      setCommercialDiscount(newCommercialDiscount)
      const newBasketData = await fetchBasket(newBasketId)
      syncSelectionFromBasket(newBasketData)
      setBasketId(newBasketId)
      setBasketInitiated(true)
      return null
    } catch {
      setResidentSwitchNonce((nonce) => nonce + 1)
      return null
    } finally {
      setBasketLoading(false)
      setResidentSwitchLoading(false)
    }
  }

  // Re-syncs the pets and extras selection atoms with the (re-priced) basket
  function syncSelectionFromBasket(basket: BffInterfacesBasket.Basket): void {
    const depOut = basket.departures[0]
    const depHome = basket.departures[1] ?? null
    const currency = basket.priceStructure.currency

    const mapDeparture = (d: BffInterfacesBasket.Departure) => ({
      arrivalDateTime: d.arrivalDateTime,
      arrivalPortCode: d.arrivalPort,
      checkinDateTime: d.checkinDateTime,
      currencyCode: currency,
      departureDate: d.departureDate,
      departureDateTimeForBasket: d.departureDateTimeForBasket,
      departurePortCode: d.departurePort,
      departurePrice: d.ticketPrice,
      departureTime: d.departureTime,
      id: d.id,
      listPosition: 0,
      offer: d.offer ? { code: d.offer.code, price: d.offer.price } : undefined,
      productCode: d.productCode,
    })

    setSelectedDepartures({
      departureHome: depHome?.id ? mapDeparture(depHome) : null,
      departureOut: depOut?.id ? mapDeparture(depOut) : null,
    })

    const numberOfPets = depOut?.pets.reduce((total, a) => total + a.quantity, 0) ?? 0
    setSelectedPets({
      pets: (depOut?.pets ?? []).map((a) => {
        const pet: SelectedPet = {
          addonCode: a.addonCode,
          addonType: a.typeCode,
          capacityInfo: { availableCapacity: 9999, capacityUnit: 'UNDEFINED', maxAllowedToReserve: 9999 },
          id: a.id,
          parentAddonCode: a.parentAddonCode,
          priceInfo: { basePrice: a.unitPriceStructure.basePrice, currency: a.unitPriceStructure.currency || currency },
          selectedQuantity: a.quantity,
        }
        return pet
      }),
      showNumberOfPets: numberOfPets,
    })

    const mapAddon = (a: BffInterfacesBasket.Addon): SelectedAddon => ({
      addonCode: a.addonCode,
      addonType: a.typeCode,
      capacityInfo: { availableCapacity: a.quantity, capacityUnit: 'PASSENGER', maxAllowedToReserve: a.quantity },
      id: a.id,
      parentAddonCode: a.parentAddonCode,
      priceInfo: { basePrice: a.unitPriceStructure.basePrice, currency: a.unitPriceStructure.currency || currency },
      selectedQuantity: a.quantity,
    })

    setSelectedAddons({
      outboundAddons: (depOut?.addons ?? []).map((a) => mapAddon(a)),
      returnAddons: (depHome?.addons ?? []).map((a) => mapAddon(a)),
    })
  }

  async function fetchBasket(basketId: string): Promise<BffInterfacesBasket.Basket> {
    const data = await getBasketEnriched(basketId, getUrlLocale())
    setBasket(data)
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('tracking-basket', JSON.stringify(data))
    }
    return data
  }

  const updateBasket = async () => {
    const basketId = getSessionBasket()?.basketId
    if (basketId) {
      setBasketLoading(true)
      await fetchBasket(basketId)
      setBasketLoading(false)
    }
  }

  return (
    <BasketContext.Provider
      value={{
        basket,
        basketId,
        basketInitiated,
        basketLoading,
        commercialDiscount,
        depDateHomebound: urlDepartures.depDateHomebound,
        recoveredBasketErrors,
        residentSwitchLoading,
        residentSwitchNonce,
        routeHomebound: urlDepartures.routeHomebound,
        toggleSogResidentVehicle,
        updateBasket,
      }}
    >
      {children}
    </BasketContext.Provider>
  )
}
